import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import React from 'react';
import {DropdownOptionProps} from 'wix-ui-tpa';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {LazyDropdown} from '../../lazyComponents/lazyWUTComponents';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import s from './OptionsDropdown.scss';
import {st, classes as stylableClasses} from './OptionsDropdown.st.css';
import classNames from 'classnames';

export interface OptionsDropdownProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  optionIndex: number;
}

export enum OptionsDropdownDataHook {
  OptionsDropdownContainer = 'options-dropdown-container',
  OptionsDropdown = 'options-dropdown',
  OptionsDropdownTitle = 'options-dropdown-title',
}

class OptionsDropdownComponent extends React.Component<OptionsDropdownProps> {
  private readonly dropdownContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: OptionsDropdownProps) {
    const {
      optionIndex,
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget =
        UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
        optionIndex === scrollAndFocusInputTarget?.index;

      if (isScrollAndFocusInputTarget && this.dropdownContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.Selection, this.dropdownContainerRef.current, isQuickView);
      }
    }
  }

  private readonly handleChange = ({id: selectionId}: DropdownOptionProps) => {
    const {
      optionIndex,
      globals: {handleUserInput, validateUserInputsChanges},
    } = this.props;

    const nextSelection = this.productOption.selections.find(({id}) => id.toString() === selectionId);

    handleUserInput(UserInputType.Selection, nextSelection, optionIndex);

    validateUserInputsChanges();
  };

  private readonly isSelectionAvailable = (isSelectable: boolean): boolean => {
    const {
      globals: {isBackInStockEnabled},
    } = this.props;

    return isBackInStockEnabled ? !isSelectable : false;
  };

  private get activeSelection() {
    const {
      optionIndex,
      globals: {userInputs},
    } = this.props;
    return userInputs[UserInputType.Selection][optionIndex];
  }

  private get productOption() {
    const {
      optionIndex,
      globals: {product},
    } = this.props;

    return product.options[optionIndex];
  }

  private get dropdownOptions(): DropdownOptionProps[] {
    const {
      globals: {variantInfo, isBackInStockEnabled},
    } = this.props;

    const getSelectionAvailabilityInfo = (selectionId: number) =>
      variantInfo.selectionsAvailability[this.productOption.id][selectionId];

    return this.productOption.selections
      .filter((selection) => getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => ({
        id: selection.id.toString(),
        isSelectable: isBackInStockEnabled ? true : getSelectionAvailabilityInfo(selection.id).isSelectable,
        value: selection.description,
        unavailable: this.isSelectionAvailable(getSelectionAvailabilityInfo(selection.id).isSelectable),
      }));
  }

  public state: Readonly<{labelForId: string}> = {
    labelForId: undefined,
  };

  public render(): JSX.Element {
    const {
      t,
      optionIndex,
      globals: {isProductSubmitted, userInputErrors, experiments, isQuickView},
    } = this.props;

    const {title, id: dropdownId} = this.productOption;
    const shouldUseUpliftSetting = experiments.productPageUpliftProductOptions;
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const dropdownRender = () => {
      return (
        <LazyDropdown
          className={classNames(st(stylableClasses.stylableRoot, {isQuickView}), {
            [stylableClasses.dropdownOptionNewStyling]: shouldUseUpliftSetting,
            [s.dropdownListNewStyling]: shouldUseUpliftSetting,
          })}
          dropdownId={dropdownId}
          label={title}
          aria-label={title}
          options={this.dropdownOptions}
          onChange={this.handleChange}
          placeholder={t('SELECT_PLACE_HOLDER')}
          initialSelectedId={(this.activeSelection?.id || '').toString()}
          data-hook={OptionsDropdownDataHook.OptionsDropdown}
          mobileNativeSelect
          newErrorMessage
          error={isProductSubmitted && userInputErrors[UserInputType.Selection][optionIndex]}
          errorMessage={t('SELECT_OPTION_WARNING', {optionTitle: title})}
          aria-required
          showAsterisk
        />
      );
    };

    return (
      <div data-hook={OptionsDropdownDataHook.OptionsDropdownContainer} ref={this.dropdownContainerRef}>
        {dropdownRender()}
      </div>
    );
  }
}

export const OptionsDropdown: React.FC<OptionsDropdownProps & ProvidedGlobalProps & IProvidedTranslationProps> =
  withGlobalProps(withTranslations('globals.texts')(OptionsDropdownComponent));
